import { Event } from '@model/event';
import { Followee } from '@model/followee';
import { Lap } from '@model/lap';
import { Participant } from '@model/participant';

import { getLastUpdatedTime, getTimeRegistrationNumber } from './lap-util';

/**
 * Compares two followees by their `lastUpdatedTime` in descending order.
 *
 * Followees without a `lastUpdatedTime` are treated as older (sorted last).
 * Time strings are parsed from HH:MM format and converted to minutes for comparison.
 *
 * @param followee1 - First followee to compare.
 * @param followee2 - Second followee to compare.
 * @returns A negative number if `followee2` is more recent, positive if `followee1` is more recent, or 0 if equal.
 */
const followeeLastUpdatedTimeComparer = (
  followee1: Followee,
  followee2: Followee,
): number => {
  // Handle cases where lastUpdated is undefined or null
  if (!followee1.lastUpdatedTime) return 1;
  if (!followee2.lastUpdatedTime) return -1;

  // Parse time string to minutes, with error handling
  const parseTimeToMinutes = (timeStr: string): number => {
    // Check if the string matches the HH:MM format
    const isValidTimeFormat = /^\d{1,2}:\d{2}$/.test(timeStr);
    if (!isValidTimeFormat) return 0;

    const parts = timeStr.split(':');
    if (parts.length !== 2) return 0;

    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);

    // Check if the parsed values are valid numbers
    if (isNaN(hours) || isNaN(minutes)) return 0;

    return hours * 60 + minutes;
  };

  const followee1Minutes = parseTimeToMinutes(followee1.lastUpdatedTime);
  const followee2Minutes = parseTimeToMinutes(followee2.lastUpdatedTime);

  // Sort in descending order (most recent first)
  return followee2Minutes - followee1Minutes;
};

/**
 * Compares two followees alphabetically by name in ascending order.
 *
 * @param followee1 - First followee to compare.
 * @param followee2 - Second followee to compare.
 * @returns A negative number if `followee1` comes first alphabetically, positive if `followee2` comes first.
 */
const followeeNameComparer = (
  followee1: Followee,
  followee2: Followee,
): number => (followee1.name < followee2.name ? -1 : 1);

/**
 * Calculates a simple progress ratio based on the number of laps.
 *
 * The value is `laps.length / 6` but never less than 0.
 *
 * @param laps - List of laps for a followee.
 * @returns A progress value between 0 and (laps.length / 6).
 */
const getLapsProgress = (laps: Lap[]): number =>
  Math.max((laps.length ?? 0) / 6, 0);

/**
 * Enriches a list of followees with lap information and derived properties.
 *
 * Laps are grouped by participant UUID and passed to `enrichFollowee` together
 * with the current event live-state. Each followee is enriched with its corresponding laps.
 *
 * @param followees - Followees to enrich.
 * @param isEventLive - Indicates whether the event is currently live.
 * @param laps - All laps for the event.
 * @returns A new array of enriched followees with updated lap information.
 */
export const enrichFollowees = (
  followees: Followee[],
  isEventLive: boolean,
  laps: Lap[],
): Followee[] => {
  const lapsMap = laps.reduce((acc, lap) => {
    const participantLaps = acc.get(lap.participantUuid) ?? [];

    acc.set(lap.participantUuid, [...participantLaps, lap]);

    return acc;
  }, new Map<string, Lap[]>());

  return followees.map(followee =>
    enrichFollowee(followee, isEventLive, lapsMap.get(followee.uuid)),
  );
};

/**
 * Creates a new followee object from a participant and optional laps.
 *
 * A followee is marked as read when there are no laps yet. `lapsProgress`
 * is derived from the number of laps (0 when there are no laps).
 *
 * @param participant - Source participant data.
 * @param laps - Optional list of laps for the participant. Defaults to empty array.
 * @returns A newly created followee with participant data and lap information.
 */
export const createFollowee = (
  participant: Participant,
  laps: Lap[] = [],
): Followee => ({
  ...participant,
  isRead: laps.length === 0,
  laps,
  lapsProgress: getLapsProgress(laps),
});

/**
 * Sorts laps by lap number based on event live status.
 *
 * When the event is live, laps are sorted in descending order (most recent first).
 * When the event is not live, laps are sorted in ascending order.
 *
 * @param laps - List of laps to sort.
 * @param isEventLive - Indicates whether the event is currently live.
 * @returns A new array of sorted laps.
 */
const sortLaps = (laps: Lap[], isEventLive: boolean): Lap[] => [
  ...laps.sort((lap1, lap2) =>
    isEventLive
      ? lap2.lapNumber - lap1.lapNumber
      : lap1.lapNumber - lap2.lapNumber,
  ),
];

/**
 * Determines whether a followee's laps have changed compared to the current laps.
 *
 * Considers number of laps, lap order and time registration changes, taking
 * into account whether the event is live when sorting. Returns false if there are no updated laps.
 *
 * @param currentLaps - Existing laps for the followee.
 * @param updatedLaps - New laps to compare with. Defaults to empty array.
 * @param isEventLive - Indicates whether the event is currently live.
 * @returns `true` when there is any difference in lap count, order, or time registration, otherwise `false`.
 */
const areLapsUpdated = (
  currentLaps: Lap[],
  updatedLaps: Lap[] = [],
  isEventLive: boolean,
): boolean => {
  // No updated laps? No difference
  if (updatedLaps.length === 0) {
    return false;
  }

  // Number of current and updated laps not the same? There is a difference
  if (currentLaps.length !== updatedLaps.length) {
    return true;
  }

  const sortedCurrentLaps = sortLaps(currentLaps, isEventLive);
  const sortedUpdatedLaps = sortLaps(updatedLaps, isEventLive);

  return sortedCurrentLaps.reduce((acc, currentLap, index) => {
    const updatedLap = sortedUpdatedLaps[index];
    if (
      updatedLap &&
      acc === false &&
      currentLap.lapNumber !== updatedLap.lapNumber
    ) {
      acc = true;
    }

    if (
      updatedLap &&
      acc === false &&
      getTimeRegistrationNumber(currentLap) !==
        getTimeRegistrationNumber(updatedLap)
    ) {
      acc = true;
    }

    return acc;
  }, false);
};

/**
 * Enriches a single followee with derived lap information.
 *
 * Calculates sorted laps, current lap, last updated time, lap progress and
 * read-state based on whether laps have been updated and if the event is live.
 * The followee is marked as unread if laps have been updated.
 *
 * @param followee - Followee to enrich.
 * @param isEventLive - Indicates whether the event is currently live.
 * @param laps - Optional laps overriding the followee's existing laps.
 * @returns An enriched followee instance with updated lap data and derived properties.
 */
export const enrichFollowee = (
  followee: Followee,
  isEventLive: boolean,
  laps?: Lap[],
): Followee => {
  const isLapsUpdated = areLapsUpdated(followee.laps, laps, isEventLive);
  const isRead = isLapsUpdated ? false : followee.isRead;

  const followeeLaps = sortLaps(laps ?? followee.laps ?? [], isEventLive);
  const currentLap = followeeLaps.length ? followeeLaps[0] : undefined;
  const lastUpdatedTime = getLastUpdatedTime(currentLap);
  const lapsProgress = getLapsProgress(followeeLaps);

  return {
    ...followee,
    laps: followeeLaps,
    currentLap,
    lastUpdatedTime,
    lapsProgress,
    isRead,
  };
};

/**
 * Sorts followees either by last updated time (when the event is live)
 * or by name (when the event is not live or undefined).
 *
 * Uses `followeeLastUpdatedTimeComparer` for live events (most recent first)
 * and `followeeNameComparer` for non-live events (alphabetical order).
 *
 * @param followees - Followees to sort.
 * @param event - Optional event used to determine whether sorting is live-based.
 * @returns A new array of sorted followees.
 */
export const sortFollowees = (
  followees: Followee[],
  event?: Event,
): Followee[] =>
  [...followees].sort(
    (event?.isEventLive ?? false)
      ? followeeLastUpdatedTimeComparer
      : followeeNameComparer,
  );

/**
 * Merges followees with updated participant data.
 *
 * For each followee, finds the corresponding participant by UUID and merges
 * the participant data into the followee. The followee's laps are explicitly
 * preserved and not overwritten by participant data. If no matching participant
 * is found, the original followee is returned unchanged.
 *
 * @param followees - Followees to merge with participant data.
 * @param participants - Optional list of participants to merge into followees.
 * @returns A new array of followees with merged participant data and preserved laps.
 */
export const mergeFollowees = (
  followees: Followee[],
  participants?: Participant[],
): Followee[] =>
  followees.map(followee => {
    const participant = participants?.find(item => item.uuid === followee.uuid);

    return participant
      ? { ...followee, ...participant, laps: followee.laps }
      : followee;
  });
